'use client';
import { useState, useEffect } from 'react';
import Image from 'next/image';
import { faShare } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CopyPostToClipboard } from './copy-post-to-clipboard.component';

import { Link } from '@/components/ui/link/link.component';
import { Icon } from '@/components/ui/icon.component';
import xSvg from '@/assets/svgs/x-logo.svg?url';
import facebookSvg from '@/assets/svgs/facebook-logo.svg?url';
import linkedinSvg from '@/assets/svgs/linkedin-logo.svg?url';
import whatsappSvg from '@/assets/svgs/whatsapp-logo.svg?url';
import telegramSvg from '@/assets/svgs/telegram-logo.svg?url';

interface ShareToSocialProps {
  url: string;
}

export function ShareToSocialMobile({ url }: ShareToSocialProps) {
  const baseUrl = typeof window !== 'undefined' ? `${window.location.origin}/${url}` : '';
  const [canUseNativeShare, setCanUseNativeShare] = useState(false);

  useEffect(() => {
    if (typeof navigator.share === 'function') {
      setCanUseNativeShare(true);
    }
  }, []);

  const handleShare = async (): Promise<void> => {
    await navigator.share({
      title: 'Blog Medprev',
      text: 'Confira esse post do blog da Medprev:',
      url: baseUrl,
    });
  };

  return (
    <div
      className="relative flex items-center justify-end w-full lg:hidden"
      data-testid={DataTestIdEnum.BLOG_SHARE_TO_SOCIAL_MOBILE}
    >
      {canUseNativeShare
        ? (
            <button
              onClick={handleShare}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-green-500 transition bg-green-100 hover:bg-green-200"
              data-testid={DataTestIdEnum.NATIVE_SHARE_BUTTON}
            >
              <Icon icon={faShare} className="w-5 h-5" />
            </button>
          )
        : (
            <div
              data-testid={DataTestIdEnum.FALLBACK_SHARE_OPTIONS}
              className="flex gap-2 items-center p-2 rounded-lg shadow-md"
            >
              <Link
                prefetch={false}
                data-testid={DataTestIdEnum.TWITTER_SHARE}
                href="https://twitter.com/intent/tweet"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={xSvg}
                  width={24}
                  height={24}
                  alt="X (Twitter) logo"
                  className="w-5 h-5 transition-transform duration-200 hover:scale-125"
                />
              </Link>
              <Link
                prefetch={false}
                data-testid={DataTestIdEnum.FACEBOOK_SHARE}
                href="https://www.facebook.com/sharer/sharer.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={facebookSvg}
                  width={24}
                  height={24}
                  alt="Facebook logo"
                  className="w-5 h-5 transition-transform duration-200 hover:scale-125"
                />
              </Link>
              <Link
                prefetch={false}
                data-testid={DataTestIdEnum.LINKEDIN_SHARE}
                href="https://www.linkedin.com/shareArticle"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={linkedinSvg}
                  width={28}
                  height={28}
                  alt="LinkedIn logo"
                  className="w-6 h-6 transition-transform duration-200 hover:scale-125"
                />
              </Link>
              <Link
                prefetch={false}
                data-testid={DataTestIdEnum.WHATSAPP_SHARE}
                href="https://api.whatsapp.com/send"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={whatsappSvg}
                  width={24}
                  height={24}
                  alt="WhatsApp logo"
                  className="w-5 h-5 transition-transform duration-200 hover:scale-125"
                />
              </Link>
              <Link
                prefetch={false}
                data-testid={DataTestIdEnum.TELEGRAM_SHARE}
                href="https://telegram.me/share/url"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={telegramSvg}
                  width={26}
                  height={26}
                  alt="Telegram logo"
                  className="w-6 h-6 transition-transform duration-200 hover:scale-125"
                />
              </Link>
              <CopyPostToClipboard url={baseUrl} />
            </div>
          )}
    </div>
  );
}
